import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { portalApi } from '../../services/portal';
import { Case, Invoice, Document, InvoiceStatus } from '../../types';
import { formatLocalDate } from '../../utils/date';

export const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'cases' | 'invoices' | 'documents'>('cases');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [casesResult, invoicesResult, docsResult] = await Promise.all([
        portalApi.getCases(),
        portalApi.getInvoices(),
        portalApi.getDocuments(),
      ]);
      setCases(casesResult.cases);
      setInvoices(invoicesResult.invoices);
      setDocuments(docsResult.documents);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const statusColors: Record<InvoiceStatus, string> = {
    draft: '#9e9e9e',
    sent: '#2196f3',
    paid: '#4caf50',
    overdue: '#f44336',
    cancelled: '#757575',
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { downloadUrl } = await portalApi.getDocumentDownloadUrl(doc.documentId);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const unpaidTotal = invoices
    .filter(inv => inv.status === 'sent' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + (inv.balanceDue ?? inv.total), 0);

  // Get unpaid invoices for notification
  const unpaidInvoices = invoices.filter(
    inv => (inv.status === 'sent' || inv.status === 'overdue') && (inv.balanceDue ?? inv.total) > 0
  );

  const handleInvoiceClick = (invoiceId: string) => {
    navigate(`/portal/invoices/${invoiceId}`);
  };

  if (loading) {
    return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <h1 style={{ marginBottom: '24px' }}>My Portal</h1>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderRadius: '8px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1976d2' }}>{cases.length}</div>
            <div style={{ color: '#666' }}>Active Cases</div>
          </div>
          <div style={{ backgroundColor: '#fff3e0', padding: '20px', borderRadius: '8px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f57c00' }}>
              {invoices.filter(i => i.status === 'sent' || i.status === 'overdue').length}
            </div>
            <div style={{ color: '#666' }}>Pending Invoices</div>
          </div>
          <div style={{ backgroundColor: '#fce4ec', padding: '20px', borderRadius: '8px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#c2185b' }}>{formatCurrency(unpaidTotal)}</div>
            <div style={{ color: '#666' }}>Amount Due</div>
          </div>
        </div>

        {/* Unpaid Invoice Notification */}
        {unpaidInvoices.length > 0 && (
          <div style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <span style={{ fontWeight: 600, color: '#856404' }}>
                You have {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {unpaidInvoices.map(inv => (
                <div 
                  key={inv.invoiceId} 
                  onClick={() => handleInvoiceClick(inv.invoiceId)}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div>
                    <span style={{ fontWeight: 500 }}>{inv.invoiceNumber}</span>
                    <span style={{ color: '#666', marginLeft: '12px' }}>
                      Due: {formatLocalDate(inv.dueDate)}
                    </span>
                    {inv.status === 'overdue' && (
                      <span style={{ 
                        backgroundColor: '#f44336', 
                        color: 'white', 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '11px', 
                        marginLeft: '8px',
                        textTransform: 'uppercase'
                      }}>
                        Overdue
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: 600, color: '#c2185b' }}>
                      ${formatCurrency(inv.balanceDue ?? inv.total)}
                    </span>
                    <span style={{ color: '#1976d2', fontSize: '14px' }}>View →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid #e0e0e0' }}>
          <button onClick={() => setActiveTab('cases')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'cases' ? '#1976d2' : 'transparent', color: activeTab === 'cases' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Cases ({cases.length})
          </button>
          <button onClick={() => setActiveTab('invoices')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'invoices' ? '#1976d2' : 'transparent', color: activeTab === 'invoices' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Invoices ({invoices.length})
          </button>
          <button onClick={() => setActiveTab('documents')} style={{ padding: '12px 24px', border: 'none', backgroundColor: activeTab === 'documents' ? '#1976d2' : 'transparent', color: activeTab === 'documents' ? 'white' : '#666', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontWeight: 500 }}>
            Documents ({documents.length})
          </button>
        </div>

        {/* Cases Tab */}
        {activeTab === 'cases' && (
          <div>
            {cases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                No cases found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {cases.map(c => (
                  <div key={c.caseId} style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '18px' }}>{c.childFirstName} {c.childLastName}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>
                          {c.school && `${c.school} • `}{c.grade && `Grade ${c.grade}`}
                        </div>
                      </div>
                      <span style={{ backgroundColor: c.status === 'active' ? '#4caf50' : '#9e9e9e', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', textTransform: 'capitalize' }}>
                        {c.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                No invoices found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {invoices.map(inv => (
                  <div key={inv.invoiceId} style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{inv.invoiceNumber}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>
                          Issued: {formatLocalDate(inv.issueDate)} • Due: {formatLocalDate(inv.dueDate)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ fontWeight: 500, fontSize: '18px' }}>{formatCurrency(inv.total)}</div>
                        <span style={{ backgroundColor: statusColors[inv.status], color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', textTransform: 'uppercase' }}>
                          {inv.status}
                        </span>
                      </div>
                    </div>
                    {inv.lineItems.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Services:</div>
                        {inv.lineItems.slice(0, 3).map((item, idx) => (
                          <div key={idx} style={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                            • {item.description} ({item.duration}m) - {formatCurrency(item.amount)}
                          </div>
                        ))}
                        {inv.lineItems.length > 3 && (
                          <div style={{ fontSize: '12px', color: '#666' }}>...and {inv.lineItems.length - 3} more items</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            {documents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                No documents found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {documents.map(doc => (
                  <div key={doc.documentId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                    <div style={{ width: '40px', height: '40px', backgroundColor: '#e3f2fd', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#1976d2', fontWeight: 500 }}>
                      {doc.fileName.split('.').pop()?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {doc.category} • {new Date(doc.uploadedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button onClick={() => handleDownload(doc)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
